function apiBase(preferred = '') {
  const root = document.getElementById('arz-portfolio-app');
  const configured = preferred || root?.dataset.nexusBase || window.arzConstants?.NEXUS_BASEURL || '';
  if (!configured) throw new Error('Nexus URL is unavailable.');
  return configured.replace(/\/$/, '');
}

async function parse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.status === 'failed' || payload.status === 'error') {
    const error = new Error(payload.message || payload.error?.message || `Request failed: ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload.data ?? payload;
}

export class PortfolioApi {
  // Pages without the /portfolio/ root (PDPs) pass their own server-rendered Nexus base.
  constructor(base = '') {
    this.base = base;
  }

  authHeaders() {
    return window.account?.headerToken?.() || {};
  }

  request(path, options = {}) {
    const headers = { Accept: 'application/json', ...this.authHeaders(), ...(options.headers || {}) };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    const fetcher = window.arzDigital2?.authenticatedFetch || window.fetch.bind(window);
    return fetcher(`${apiBase(this.base)}${path}`, { credentials: 'omit', ...options, headers }).then(parse);
  }

  portfolios() { return this.request('/v1/portfolio'); }
  detail(id) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}`); }
  create(values) { return this.request('/v1/portfolio', { method: 'POST', body: JSON.stringify({ ...values, is_shared: false }) }); }
  update(id, values) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ ...values, is_shared: false }) }); }
  deletePortfolio(id) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
  chart(id, range) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}/charts/line?range=${encodeURIComponent(range)}`); }
  asset(id, assetId) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}`); }
  transactions(id, assetId) { return this.request(`/v1/portfolio/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}/transactions`); }
  addTransaction(id, assetId, values) {
    return this.request(`/v1/portfolio/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}/transactions`, { method: 'POST', body: JSON.stringify(values) });
  }
  updateTransaction(id, transactionId, values) {
    return this.request(`/v1/portfolio/${encodeURIComponent(id)}/transactions/${encodeURIComponent(transactionId)}`, { method: 'PATCH', body: JSON.stringify(values) });
  }
  deleteTransaction(id, transactionId) {
    return this.request(`/v1/portfolio/${encodeURIComponent(id)}/transactions/${encodeURIComponent(transactionId)}`, { method: 'DELETE' });
  }
  deleteAsset(id, assetId) {
    return this.request(`/v1/portfolio/${encodeURIComponent(id)}/assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' });
  }
  historicalQuote(assetId, timestampSeconds, signal) {
    const query = new URLSearchParams({
      id: String(assetId), from: String(timestampSeconds), to: String(timestampSeconds), interval: '1d',
    });
    return this.request(`/v1/pub/coins/chart?${query}`, { signal });
  }
  mappedAssets(ids, signal) {
    return this.request(`/v1/pub/coins/mapped?ids=${ids.map(encodeURIComponent).join(',')}`, { signal });
  }
  currentAsset(slug, signal) {
    return this.request(`/v1/pub/coins/${encodeURIComponent(slug)}`, { signal, cache: 'no-store' });
  }
}

